import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import type { AnswerOut, CategoryShare } from '@/services/types';
import { cn } from '@/lib/cn';

/**
 * A horizontal bar list rather than a pie, on purpose. The codes are
 * MULTI-LABEL: one decision can carry both WARNING and FINE, so the shares add
 * to more than 100% and a pie would be arithmetically impossible to draw
 * honestly.
 *
 * `UNCLASSIFIED` is rendered like any other row and never dropped. It is the
 * largest single category after fines and warnings, and hiding it would present
 * the classified fraction as if it were the whole corpus.
 */
export function OutcomeMixPanel({
  answer,
  titleKey = 'anatel:overview.outcomeMix.title',
  helpKey = 'anatel:overview.outcomeMix.help',
  labelPrefix = 'anatel:overview.outcomes.',
  multiLabelKey,
  helpParams,
  rowHref,
}: {
  answer: AnswerOut<CategoryShare[]>;
  /** Overridden by the appeal-disposition panel, which is the same shape. */
  titleKey?: string;
  helpKey?: string;
  /**
   * Disclosure for shares that sum past 100%, rendered ONLY when the counts
   * actually outnumber the denominator.
   *
   * `Answer` deliberately drops `answer.notes` (English prose from the backend)
   * so that every visible caveat comes from a locale file -- which makes `help`
   * the only channel, and means a caveat the backend emits but `help` omits is
   * invisible on screen. That is what happened here: the appeal panel's backend
   * emits a multi-label note, `overview.appeals.help` never mentioned it, and
   * the page rendered five dispositions summing to 106% looking like an
   * arithmetic fault.
   *
   * Computed from the same two numbers the panel prints, following
   * `aneel:bidding.ti.multiLabel`, so it disappears by itself if the source ever
   * becomes single-label rather than asserting a permanent property.
   */
  multiLabelKey?: string;
  /**
   * Values interpolated into `helpKey`, for a caption whose figures must be
   * measured rather than written into three locale files.
   *
   * Formatted here through `Intl.NumberFormat` rather than handed to i18next
   * raw, so a thousands separator follows the reader's locale like every other
   * number on the page.
   */
  helpParams?: Record<string, number>;
  /**
   * Namespace for the per-code labels. Each agency has its own outcome
   * vocabulary -- ANEEL's penalty trajectories share nothing with ANATEL's
   * outcome codes -- so the panel takes the prefix rather than assuming one.
   * This is the shared-component half of "one product with an agency dimension".
   */
  labelPrefix?: string;
  /**
   * Where a row opens: the list filtered to that code, or nothing. A share is
   * a claim about rows, and a claim a reader can click through to its rows is
   * one they can check; a code with no list behind it stays plain text.
   */
  rowHref?: (code: string) => string | null;
}) {
  const { t, i18n } = useTranslation(['anatel', 'aneel', 'common']);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <section className="rounded-lg border bg-card p-4">
      <h2 className="mb-3 text-sm font-semibold text-foreground">{t(titleKey)}</h2>
      <Answer
        answer={answer}
        help={t(
          helpKey,
          helpParams
            ? Object.fromEntries(
                Object.entries(helpParams).map(([key, value]) => [key, number.format(value)]),
              )
            : undefined,
        )}
      >
        {(rows) => {
          const labels = rows.reduce((sum, row) => sum + row.count, 0);
          const denominator = answer.population.total;
          const multiLabelled = multiLabelKey && labels > denominator;
          return (
            <>
          <ul className="space-y-2">
            {rows.map((row) => {
              const label = t(`${labelPrefix}${row.code}`, { defaultValue: row.code });
              const unclassified = row.code === 'UNCLASSIFIED';
              const href = rowHref ? rowHref(row.code) : null;
              return (
                <li key={row.code} className="space-y-1">
                  <div className="flex items-baseline justify-between gap-4 text-sm">
                    {href ? (
                      <Link
                        to={href}
                        className={cn(
                          'truncate underline-offset-2 hover:underline',
                          unclassified ? 'italic text-muted-foreground' : 'text-primary',
                        )}
                        data-outcome-link={row.code}
                      >
                        {label}
                      </Link>
                    ) : (
                      <span
                        className={cn(
                          'truncate',
                          unclassified ? 'italic text-muted-foreground' : 'text-foreground',
                        )}
                      >
                        {label}
                      </span>
                    )}
                    <span className="shrink-0 tabular-nums text-muted-foreground">
                      {number.format(row.count)} · {percent.format(row.share)}
                    </span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded bg-muted">
                    <div
                      className={cn('h-full rounded', unclassified ? 'bg-muted-foreground/50' : 'bg-primary')}
                      style={{ width: `${Math.min(row.share * 100, 100)}%` }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
              {multiLabelled ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  {t(multiLabelKey, {
                    decisions: number.format(denominator),
                    labels: number.format(labels),
                  })}
                </p>
              ) : null}
            </>
          );
        }}
      </Answer>
    </section>
  );
}
